import { ReactElement } from "react";
import Chart from "../../../components/charts/chart";
import { getTicks, useChartDimensions, useUniqueId } from "../../../components/charts/chartUtils";
import * as d3 from "d3";
import AxisHorizontal from "../../../components/charts/AxisHorizontal";
import {
  IDelayBase, IDelayQMOS,
  IStrandProduction,
  IVariableGroup
} from "../../../types";
import KeyValueItem from "../../../components/charts/keyValueItem";
import { formatTimeWithSeconds, getCurrentDatetimeInSeconds } from "../../../system/utils";
import { getPalette } from "../../../configuration/configuration";
import {
  xProductionBeginAccessorScaled,
  xProductionEndAccessorScaled, xSampleAccessorScaled
} from "../../production/utils";
import { IMillProductDetail } from "../types";
import HtmlTooltip from "../../../components/charts/tooltip";
import DelayLayerGantt from "../../interruptions/compoenents/delayLayerGantt";
import VariablesLayerGantt from "../../production/variablesLayerGantt";
import SamplesLayerGantt from "../../production/components/samplesLayerGantt";

interface IHeatChartProps {
  width?: number;
  productionData: IStrandProduction;
  hideAxisTitle?: boolean;
  products: IMillProductDetail[] | null;
  setSelectedItem: (item: IMillProductDetail) => void;
  selectedItem: IMillProductDetail | null;
  selectedDelay: IDelayQMOS | null;
  setSelectedDelay: (delay: IDelayQMOS | null) => void;
}

const MillOrderChart = ({ width, productionData, hideAxisTitle, products, setSelectedItem, selectedItem, selectedDelay, setSelectedDelay }: IHeatChartProps): ReactElement => {
  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    width,
    height: 110,
    marginBottom: 20,
    marginLeft: 30,
    marginRight: 30,
    marginTop: 10
  });

  const endTimeList = productionData.delays.map(d => d.endDatetime ?? getCurrentDatetimeInSeconds());
  const end = Math.max(...endTimeList, productionData.endDatetime ?? getCurrentDatetimeInSeconds());

  const xScale = d3.scaleLinear()
    .domain([productionData.beginDatetime, end])
    .range([0, dimensions.boundedWidth]);


  const xAccessorScaled = (d: IDelayBase) => xScale(d.beginDatetime);
  const widthAccessorScaled = (d: IDelayBase) => xScale(d.endDatetime ?? getCurrentDatetimeInSeconds()) - xScale(d.beginDatetime);

  const heatKey = useUniqueId("mill-order-chart");
  const heatY = 58;

  const variables: IVariableGroup[] = [];
  productionData.variables.forEach(variable => {
    const item = variables.find(x => x.datetime === variable.datetime);
    if (!item) {
      variables.push({
        datetime: variable.datetime,
        variables: [ variable ]
      });
    } else {
      item.variables.push(variable);
    }
  });

  let totalDelays = 0;
  productionData.delays.filter(xyz => !xyz.isDownDay).forEach(delay => {
    totalDelays += delay.duration ?? 0;
  });

  const barInMill = ((productionData.endDatetime ?? getCurrentDatetimeInSeconds()) - productionData.beginDatetime - totalDelays) / 60;


  return (
    <div ref={ref}>
      <Chart dimensions={dimensions}>
        <AxisHorizontal
          chartID={heatKey}
          dimensions={dimensions}
          scale={xScale}
          label={!hideAxisTitle ? `${productionData.title}` : ""}
          ticks={getTicks(xScale.domain(), dimensions.boundedWidth > 400 ? 5 : 3)}
          formatTick={(value) => formatTimeWithSeconds(value as number)}/>

        <g>

          <HtmlTooltip title={
            <div>
              <div className="display-flex-column align-items-center" >
                <strong>Detail</strong>
                <strong>{productionData.title}</strong>
              </div>
              <KeyValueItem name={"Begin"} value={formatTimeWithSeconds(productionData.beginDatetime as number)} />
              <KeyValueItem name={"End"} value={formatTimeWithSeconds(productionData.endDatetime as number)} />
              <KeyValueItem name={"Bar in Mill"} value={barInMill.toFixed(1)} unit="min"/>
              <KeyValueItem name={"Heat Number"} value={productionData.heatNumber}/>
              <KeyValueItem name={"Steel"} value={productionData.steelCode}/>
            </div>
          }>
            <g>
              <rect
                fill={getPalette().statusColor.production.background}
                x={xProductionBeginAccessorScaled(productionData, xScale)}
                y={heatY}
                width={xScale(end) - xProductionBeginAccessorScaled(productionData, xScale)}
                height={20}
              />
            </g>
          </HtmlTooltip>

          <DelayLayerGantt delays={productionData.delays} height={20} y={heatY} xAccessorScaled={xAccessorScaled}
            widthAccessorScaled={widthAccessorScaled} selected={selectedDelay} chartKey={heatKey} setSelected={(d) => setSelectedDelay(d as IDelayQMOS)} />

          <SamplesLayerGantt chartID={heatKey} title={productionData.title} samples={productionData.samples} y={heatY - 13} xAccessorScaled={sample => xSampleAccessorScaled(sample, xScale) - 4} />

          <VariablesLayerGantt chartID={heatKey} title={productionData.title} variableGroups={variables} y={heatY - 13} xAccessorScaled={variableGroup => xScale(variableGroup.datetime) - 5} />

          {products && products.map((item, index) => {

            const p1 = xProductionBeginAccessorScaled(item, xScale);
            const p2 = xProductionEndAccessorScaled(item, xScale);
            const fill = item === selectedItem ? getPalette().statusColor.selected.background : "gray";

            return(
              <g key={`detail-prod-${index}`}>
                <HtmlTooltip title={
                  <div>
                    <div className="display-flex-column align-items-center" >
                      <strong>Coil</strong>
                      <strong>{productionData.title}</strong>
                      <KeyValueItem name={"Begin"} value={formatTimeWithSeconds(item.beginDatetime as number)} />
                      <KeyValueItem name={"End"} value={formatTimeWithSeconds(item.endDatetime as number)} />
                      <KeyValueItem name={"Billet"} value={`${item.billet}`} />
                      <KeyValueItem name={"Weight"} value={`${item.weight}`} unit="lb"/>
                    </div>
                  </div>
                }>
                  <g transform={`translate(${xProductionBeginAccessorScaled(item, xScale)}, ${0})`} cursor="pointer"
                    onClick={() => setSelectedItem(item)}>
                    <circle cx={(p2 - p1) / 2} cy="35" r="6" fill={fill}/>

                    <polygon fill={"#777"} stroke={"#777"}
                      points={`0 45 0 55 5 50 ${p2 - p1 - 5} 50 ${p2 - p1} 55 ${p2 - p1} 45`}/>
                  </g>
                </HtmlTooltip>
              </g>
            );
          })
          }

          <text y={heatY + 15}
            x={xProductionBeginAccessorScaled(productionData, xScale) + (xProductionEndAccessorScaled(productionData, xScale) - xProductionBeginAccessorScaled(productionData, xScale)) / 2 }
            fontSize="1em"
            textAnchor="middle"
            fill={"white"}
            fontWeight={"bold"}>
            {productionData.title}
          </text>
        </g>
      </Chart>
    </div>
  );
};

export default MillOrderChart;
